import { Clock } from 'lucide-react-native'
import type { Control, FieldValues, Path, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { StyleSheet, View } from 'react-native'
import { Divider, Switch, Text, useTheme } from 'react-native-paper'
import { TimeInput, WorkingDaysSelector } from '@/ui/form'

interface WorkingHoursFormProps<T extends FieldValues> {
    control: Control<T>
    watch: UseFormWatch<T>
    setValue: UseFormSetValue<T>
    enabledFieldName: Path<T>
    startTimeFieldName: Path<T>
    endTimeFieldName: Path<T>
    workingDaysFieldName: Path<T>
    disabled?: boolean
    showEnableToggle?: boolean
    title?: string
    description?: string
}

export function WorkingHoursForm<T extends FieldValues>({
    control,
    watch,
    setValue,
    enabledFieldName,
    startTimeFieldName,
    endTimeFieldName,
    workingDaysFieldName,
    disabled = false,
    showEnableToggle = true,
    title = 'Working Hours',
    description = 'Set the hours when location tracking is active.',
}: WorkingHoursFormProps<T>) {
    const theme = useTheme()
    const isEnabled = watch(enabledFieldName)

    return (
        <View style={styles.container}>
            <View style={styles.header}>
                <Clock size={18} color={theme.colors.onSurfaceVariant} />
                <Text variant="titleMedium" style={styles.title}>
                    {title}
                </Text>
            </View>
            <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
                {description}
            </Text>

            {showEnableToggle && (
                <View style={styles.toggleRow}>
                    <Text variant="bodyMedium">Enable Working Hours</Text>
                    <Switch
                        value={!!isEnabled}
                        onValueChange={value =>
                            setValue(enabledFieldName, value as T[Path<T>], { shouldDirty: true })
                        }
                        disabled={disabled}
                    />
                </View>
            )}

            {isEnabled && (
                <>
                    <Divider style={styles.divider} />
                    <View style={styles.timeRow}>
                        <TimeInput
                            name={startTimeFieldName}
                            control={control}
                            label="Start Time"
                            disabled={disabled}
                            wrapperProps={{ style: styles.timeInput }}
                        />
                        <TimeInput
                            name={endTimeFieldName}
                            control={control}
                            label="End Time"
                            disabled={disabled}
                            wrapperProps={{ style: styles.timeInput }}
                        />
                    </View>
                    <WorkingDaysSelector
                        name={workingDaysFieldName}
                        control={control}
                        label="Working Days"
                        disabled={disabled}
                    />
                </>
            )}
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        gap: 12,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    title: {
        fontWeight: '600',
    },
    toggleRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    divider: {
        marginVertical: 4,
    },
    timeRow: {
        flexDirection: 'row',
        gap: 16,
    },
    timeInput: {
        flex: 1,
    },
})
